import { useState, useMemo } from 'react'
import { apiGet } from '../../../../../generic/Api_Functions'
import { useSmartInterval } from '../../../../../generic/hooks/useSmartInterval'
import { formatReference } from '../utils/helpers'

/**
 * Custom hook for fetching and managing device data
 * @param {string|number} deviceId - Selected device ID
 * @param {boolean} showPanel - Indicates if the detail panel is visible (for optimizing data loading)
 * @returns {object} - Object containing device data, loading state, and error
 */
export const useDeviceData = (deviceId, showPanel = true) => {
  const [deviceData, setDeviceData] = useState(null)
  const [error, setError] = useState(null)

  // Función de obtención de datos para useSmartInterval
  /**
   * Function for fetching device data using API
   * @async
   * @returns {Promise<object|null>} - Device data or null if error
   */
  const fetchData = async () => {
    if (!deviceId) return null
    try {
      // setLoading(true) // Avoid flickering loading during polling
      const result = await apiGet(`mos/getDeviceDataById/${deviceId}/${showPanel}`)
      return result
    } catch (err) {
      console.error('Error fetching device data:', err)
      setError(err)
      return null
    } finally {
      // setLoading(false)
    }
  }

  // Polling every 1000ms
  useSmartInterval(fetchData, 1000, (result) => {
    if (result) {
      setDeviceData(result)
      setError(null)
    }
  })

  // Memoized data processing to avoid unnecessary recalculations
  const processedData = useMemo(() => {
    if (!deviceData) return null

    const foPrList = deviceData?.jobAndFabricationOrders?.map(item =>
      `${item.fabricationOrder || '-'} / ${item.partReference || '-'}`
    ) || []

    const jobReferences = deviceData?.jobAndFabricationOrders
      ?.map(item => item.jobReference)
      .filter(Boolean) || []

    const operators = deviceData?.workerNames || []

    const deviceTypeName = typeof deviceData?.deviceType === 'object'
      ? deviceData?.deviceType?.name
      : deviceData?.deviceType

    return {
      raw: deviceData,
      status: deviceData?.deviceStatus || '-',
      mode: deviceData?.mode,
      deviceType: deviceTypeName,
      rows: {
        active: formatReference(foPrList),
        part: formatReference(jobReferences),
        operator: operators.join(', ') || '-',
        deviceType: deviceTypeName
      }
    }
  }, [deviceData])

  return {
    data: processedData,
    loading: !deviceData, // Initial loading state if desired
    error
  }
}
